import * as React from "react";
import { getJSON } from "./network";
import tokenStore from "./tokenStore";
import { refreshTokens } from "services/idAuth";
import { ACTION_SET_USER, ACTION_USER_LOADED, ACTION_USER_LOGOUT, SessionContext, USER_STATUS_LOGGED_IN, USER_STATUS_NO_INFO } from "./session";

const AuthContext = React.createContext();

export function useAuth() {
    const session = React.useContext(SessionContext);

    const getAuthenticationStatus = async () => {
        const status = session.auth.data.authenticationStatus;
        // Revalidate when we have no info yet, or when the session still says
        // "logged in" but the access token has since expired — the latter
        // happens on client-side navigation, where the reducer state persists
        // across route changes and would otherwise never be rechecked. login()
        // silently refreshes (via the HttpOnly cookie) and only falls back to
        // logged-out if that fails.
        if (status === USER_STATUS_NO_INFO ||
            (status === USER_STATUS_LOGGED_IN && !tokenStore.isAuthenticated())) {
            await login();
        }
        return session.auth.data.authenticationStatus;
    };

    // Populates the session from the tokens held in the store (obtained via the
    // id code exchange). No tokens / expired -> logged out.
    const login = () => {
        return new Promise((res) => {
            const loggedOut = () => {
                session.auth.dispatcher({ type: ACTION_USER_LOGOUT });
                res(session.auth.data.authenticationStatus);
            };

            const hydrate = () => {
                getJSON(process.env.REACT_APP_API_ROOT + "v3/profiles/me")
                    .then(response => {
                        session.auth.dispatcher({
                            type: ACTION_SET_USER, payload: {
                                givenName: response.given_name,
                                familyName: response.family_name,
                                emailAddress: response.email_address,
                                unreadNotifications: response.newNotifications,
                                userGroups: tokenStore.getGroups()
                            }
                        });
                        session.auth.dispatcher({ type: ACTION_USER_LOADED });
                        res(session.auth.data.authenticationStatus);
                    })
                    .catch(loggedOut);
            };

            if (tokenStore.isAuthenticated()) {
                hydrate();
                return;
            }
            // Access token missing/expired. If a session existed, try a silent
            // refresh via the HttpOnly cookie before treating it as logged out.
            if (tokenStore.getTokens()) {
                refreshTokens().then(refreshed => (refreshed ? hydrate() : loggedOut()));
                return;
            }
            loggedOut();
        });
    };

    const getProfile = () => {
        return {
            givenName: session.auth.data.user.givenName,
            familyName: session.auth.data.user.familyName,
            emailAddress: session.auth.data.user.emailAddress,
            userGroups: session.auth.data.user.userGroups
        };
    }

    return {
        isAuthenticated() {
            return session.auth.data.isAuthenticated;
        },
        authenticationStatus: session.auth.data.authenticationStatus,
        getAuthenticationStatus,
        login,
        logout() {
            return new Promise((res) => {
                tokenStore.clearTokens();
                session.auth.dispatcher({ type: ACTION_USER_LOGOUT });
                res();
            });
        },
        getProfile
    };
}

export function AuthProvider({ children }) {
    const auth = useAuth();

    return (
        <AuthContext.Provider value={auth}>
            {children}
        </AuthContext.Provider>
    );
}
